import { useEffect, useState } from 'react';
import { workerApi, siteApi, workerSiteAssignmentApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const EMPTY_FORM = { companyName: '' };

// Subcontractors are Worker rows with workerCategory=SUBCONTRACTOR — see
// materialwatch-system/backend/prisma/schema.prisma. This page is their
// dedicated CRUD screen (Add/Edit/Delete), gated entirely by
// manage_subcontractors (route-level in App.jsx and again here per-action,
// since the backend also enforces it independently). There is deliberately
// no separate "Subcontractors" tab under Inventory — they appear alongside
// Employees/Contract Employees in the unified Workers tab there, badged.
//
// Subcontractors are tracked at the COMPANY level, not the individual
// employee level — a subcontractor company can be issued any quantity of
// material (no per-worker cap applies, see workerKits.js/stationery.js), and
// there's no employee name/role to collect. Worker.name (NOT NULL) is
// mirrored from companyName server-side on every create/update, so the
// Worker.id itself doubles as the company's stable, non-editable, unique
// identifier — every relationship (site assignments, issuances, deductions,
// audit logs) is already keyed off it, so renaming the company here never
// breaks history elsewhere.
export default function SubcontractorsPage() {
  const [rows, setRows]         = useState([]);
  const [sites, setSites]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [editing, setEditing]   = useState(null); // null = closed, {} = new, {...} = editing
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  // Renaming an EXISTING company is its own inline action, independent from
  // the Site Assignments section below (which already saves immediately per
  // click) — toggled on demand rather than being a permanently-live input
  // sitting in the same form, so typing a new name can't be disturbed by a
  // re-render triggered by an unrelated site add/remove elsewhere in the modal.
  const [renaming, setRenaming]     = useState(false);
  const [nameDraft, setNameDraft]   = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError]   = useState('');

  // Site assignment, managed inline from the Edit modal — only meaningful
  // once the subcontractor already exists (has an id), since a new company
  // has to be saved before it can hold a WorkerSiteAssignment row.
  const [addSiteId, setAddSiteId]   = useState('');
  const [siteActionError, setSiteActionError] = useState('');
  const [siteActionBusy, setSiteActionBusy]   = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [deleteError, setDeleteError]   = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workerApi.list();
      setRows((res.data.data || []).filter((w) => w.workerCategory === 'SUBCONTRACTOR'));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    siteApi.list().then((res) => setSites(res.data.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing({});
    setForm(EMPTY_FORM);
    setFormError('');
    setAddSiteId('');
    setSiteActionError('');
    setRenaming(false);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ companyName: w.companyName || '' });
    setFormError('');
    setAddSiteId('');
    setSiteActionError('');
    setRenaming(false);
    setNameError('');
  };

  const startRename = () => {
    setNameDraft(editing.companyName || '');
    setNameError('');
    setRenaming(true);
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { setNameError('Company name is required'); return; }
    setNameSaving(true);
    setNameError('');
    try {
      await workerApi.update(editing.id, { companyName: trimmed, workerCategory: 'SUBCONTRACTOR' });
      setEditing((prev) => ({ ...prev, companyName: trimmed }));
      setRenaming(false);
      await load();
    } catch (err) {
      setNameError(err.response?.data?.message || 'Failed to rename subcontractor');
    } finally {
      setNameSaving(false);
    }
  };

  const handleAddSite = async () => {
    if (!addSiteId || !editing?.id) return;
    setSiteActionBusy(true);
    setSiteActionError('');
    try {
      await workerSiteAssignmentApi.create({ workerId: editing.id, siteId: parseInt(addSiteId, 10) });
      const site = sites.find((s) => s.id === parseInt(addSiteId, 10));
      setEditing((prev) => ({
        ...prev,
        siteAssignments: [...(prev.siteAssignments || []), { siteId: site.id, site }],
      }));
      setAddSiteId('');
      await load();
    } catch (err) {
      setSiteActionError(err.response?.data?.message || 'Failed to assign site');
    } finally {
      setSiteActionBusy(false);
    }
  };

  const handleRemoveSite = async (siteId) => {
    if (!editing?.id) return;
    setSiteActionBusy(true);
    setSiteActionError('');
    try {
      await workerSiteAssignmentApi.remove(editing.id, siteId);
      setEditing((prev) => ({
        ...prev,
        siteAssignments: (prev.siteAssignments || []).filter((a) => a.siteId !== siteId),
      }));
      await load();
    } catch (err) {
      setSiteActionError(err.response?.data?.message || 'Failed to unassign site');
    } finally {
      setSiteActionBusy(false);
    }
  };

  // Only the CREATE path — editing an existing company's name goes through
  // handleSaveName instead (see above).
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      await workerApi.create({ companyName: form.companyName, workerCategory: 'SUBCONTRACTOR' });
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save subcontractor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await workerApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete subcontractor');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const q = search.toLowerCase().trim();
  const displayed = q
    ? rows.filter((w) => (w.companyName || '').toLowerCase().includes(q))
    : rows;

  return (
    <div>
      <PageHeader
        title="Manage Subcontractors"
        subtitle="Add, edit, and remove subcontractors, and manage which sites each company is assigned to via Edit."
        action={<button className="btn-primary" onClick={openCreate}>+ Add Subcontractor</button>}
      />

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

      <div className="mb-4">
        <input
          type="text"
          className="input w-64 !py-1.5 !text-sm"
          placeholder="Search by company name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Sites</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((w) => (
              <tr key={w.id}>
                <td className="font-medium">{w.companyName || '—'}</td>
                <td>
                  {w.siteAssignments?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {w.siteAssignments.map((a) => (
                        <span key={a.siteId ?? a.id} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          {a.site?.name}
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-gray-300 text-sm">None</span>}
                </td>
                <td><StatusBadge status={w.isActive ? 'active' : 'inactive'} /></td>
                <td>
                  <div className="flex gap-3">
                    <button className="text-indigo-600 hover:underline text-sm font-medium" onClick={() => openEdit(w)}>Edit</button>
                    <button className="text-red-500 hover:underline text-sm font-medium" onClick={() => { setDeleteTarget(w); setDeleteError(''); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && (
              <tr><td colSpan={4} className="text-center py-10 text-gray-400">
                {search ? `No subcontractors match "${search}"` : 'No subcontractors yet. Use "+ Add Subcontractor" to add one.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit */}
      {editing && (
        <Modal
          title={editing.id ? 'Edit Subcontractor' : 'Add Subcontractor'}
          onClose={() => setEditing(null)}
        >
          {!editing.id ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Company Name *</label>
              <input className="input" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} required autoFocus />
              <p className="text-xs text-gray-400 mt-1">
                Subcontractors are tracked at the company level — materials are issued directly to the company, with no employee-level tracking or quantity limit.
              </p>
            </div>
            {formError && <p className="text-red-600 text-sm">{formError}</p>}
            <ModalActions
              onCancel={() => setEditing(null)}
              submitLabel={saving ? 'Saving…' : 'Add Subcontractor'}
              disabled={saving || !form.companyName.trim()}
            />
          </form>
          ) : (
          <div className="space-y-4">
            <div>
              <label className="label">Company Name</label>
              {renaming ? (
                <div>
                  <div className="flex gap-2">
                    <input
                      className="input flex-1"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      autoFocus
                      disabled={nameSaving}
                    />
                    <button type="button" className="btn-secondary !py-1.5 !text-sm" onClick={() => setRenaming(false)} disabled={nameSaving}>Cancel</button>
                    <button type="button" className="btn-primary !py-1.5 !text-sm" onClick={handleSaveName} disabled={nameSaving || !nameDraft.trim()}>
                      {nameSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {nameError && <p className="text-red-600 text-sm mt-1">{nameError}</p>}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-900 font-medium">{editing.companyName}</p>
                  <button type="button" className="text-indigo-600 hover:underline text-sm font-medium" onClick={startRename}>Edit</button>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Subcontractors are tracked at the company level — materials are issued directly to the company, with no employee-level tracking or quantity limit.
              </p>
            </div>

            {editing.id && (
              <div className="border-t border-gray-100 pt-4">
                <label className="label">Site Assignments</label>
                {editing.siteAssignments?.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {editing.siteAssignments.map((a) => (
                      <span key={a.siteId} className="flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {a.site?.name}
                        <button
                          type="button"
                          className="text-purple-500 hover:text-purple-900 leading-none"
                          onClick={() => handleRemoveSite(a.siteId)}
                          disabled={siteActionBusy}
                          title="Unassign from this site"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 mb-3">Not assigned to any site yet.</p>
                )}

                <div className="flex gap-2">
                  <select
                    className="input flex-1 !py-1.5 !text-sm"
                    value={addSiteId}
                    onChange={(e) => setAddSiteId(e.target.value)}
                  >
                    <option value="">Select a site to add…</option>
                    {sites
                      .filter((s) => !editing.siteAssignments?.some((a) => a.siteId === s.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className="btn-secondary !py-1.5 !text-sm"
                    onClick={handleAddSite}
                    disabled={!addSiteId || siteActionBusy}
                  >
                    Add
                  </button>
                </div>
                {siteActionError && <p className="text-red-600 text-sm mt-2">{siteActionError}</p>}
              </div>
            )}

            <div className="flex justify-end pt-2 border-t border-gray-100">
              <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Close</button>
            </div>
          </div>
          )}
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <Modal title="Delete Subcontractor" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600 mb-4">
            Delete <strong>{deleteTarget.companyName}</strong>? Any active materials/stationery must be returned first.
          </p>
          {deleteError && <p className="text-red-600 text-sm mb-3">{deleteError}</p>}
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Shared sub-components (same convention as WorkerList.jsx/InventoryPage.jsx) ──

const Modal = ({ title, subtitle, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel, disabled = false }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary" disabled={disabled}>{submitLabel}</button>
  </div>
);
